import { errorRedirect, loginRedirect } from '../redirect-config';
import type { RouterContext } from '../router';
import { unhandledAuthStatus } from '../routerAuth';

export interface AuthenticatedGuardArgs {
  context: RouterContext;
  location: { pathname: string; href: string };
}

/**
 * A side-effecting guard run from `_authenticated.beforeLoad`. Throw a
 * `redirect(...)` to abort navigation, otherwise return void.
 */
export type AuthenticatedGuard = (args: AuthenticatedGuardArgs) => void;

export const requireAuthentication: AuthenticatedGuard = ({ context, location }) => {
  switch (context.auth.status) {
    case 'authenticated':
      return;
    case 'unresolved':
      throw errorRedirect();
    case 'anonymous':
      // The route being entered, not `window.location`, which is still the page being left.
      throw loginRedirect({ redirectTo: location.href });
    default:
      unhandledAuthStatus(context.auth);
  }
};

/**
 * Builds the `beforeLoad` for the `_public` layout: the domain guards without the authentication
 * requirement. Public routes are reachable while signed in, so a gate registered only on
 * `_authenticated` is bypassed by any public deep-link.
 */
export function composePublicBeforeLoad(extraGuards: readonly AuthenticatedGuard[]) {
  return function publicBeforeLoad(args: AuthenticatedGuardArgs) {
    for (const guard of extraGuards) {
      guard(args);
    }
  };
}

/**
 * Builds the `beforeLoad` for the `_authenticated` layout: enforces authentication first, then runs
 * each domain-supplied guard. Domains register their guards by exporting them from their
 * `route.manifest.ts`; `routeTree.ts` collects and injects them.
 *
 * Lives in its own file so the spec can import and exercise the real guard (rather than duplicating
 * its body), and so this module stays free of domain-specific imports.
 */
export function composeAuthenticatedBeforeLoad(extraGuards: readonly AuthenticatedGuard[]) {
  return composePublicBeforeLoad([requireAuthentication, ...extraGuards]);
}
